import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Clock, Upload, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GardeUploadSection = () => {
  const [uploading, setUploading] = useState(false);
  const [gardeMonth, setGardeMonth] = useState('');
  const [currentGarde, setCurrentGarde] = useState(null);
  const fileInputRef = useRef(null);
  const { token } = useAuth();

  useEffect(() => {
    fetchCurrentGarde();
  }, []);

  const fetchCurrentGarde = async () => {
    try {
      const response = await axios.get(`${API_URL}/pharmacy/garde`);
      setCurrentGarde(response.data);
    } catch (error) {
      console.error('Error fetching garde data:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !gardeMonth) {
      toast.error('Veuillez sélectionner un mois et un fichier PDF');
      return;
    }

    if (!file.name.endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('month', gardeMonth);

    try {
      const response = await axios.post(`${API_URL}/pharmacy/garde/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${response.data.total_pharmacies} pharmacies de garde importées`);
      fetchCurrentGarde();
      setGardeMonth('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const months = [
    'Janvier 2026', 'Février 2026', 'Mars 2026', 'Avril 2026', 'Mai 2026', 'Juin 2026',
    'Juillet 2026', 'Août 2026', 'Septembre 2026', 'Octobre 2026', 'Novembre 2026', 'Décembre 2026'
  ];

  return (
    <div className="p-4 bg-gradient-to-r from-[#1E3A5F]/10 to-[#1E3A5F]/5 rounded-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#1E3A5F]/20 rounded-lg flex items-center justify-center">
          <Clock className="w-5 h-5 text-[#1E3A5F]" />
        </div>
        <div>
          <p className="font-medium text-[#5D4E37]">Pharmacies de Garde</p>
          <p className="text-sm text-[#8B7355]">
            {currentGarde?.month ? `Actuel: ${currentGarde.month} (${currentGarde.total_pharmacies || 0} pharmacies)` : 'Aucune donnée'}
          </p>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-3">
        <select
          value={gardeMonth}
          onChange={(e) => setGardeMonth(e.target.value)}
          className="flex-1 min-w-[150px] p-2 border border-[#E8DDD4] rounded-lg text-sm"
        >
          <option value="">Sélectionner le mois</option>
          {months.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        
        <input
          type="file"
          accept=".pdf"
          onChange={handleUpload}
          ref={fileInputRef}
          className="hidden"
          id="garde-file-input"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || !gardeMonth}
          className="bg-[#1E3A5F] hover:bg-[#162D4A]"
          data-testid="upload-garde-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Import...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Uploader PDF
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default GardeUploadSection;
